/* eslint-disable react-hooks/exhaustive-deps */
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import authProfileState from "../../states/authProfileState";
import { useRecoilValue } from "recoil";
import getSchema from "../../lib/api/getSchema";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import layoutConf from "../../config/layoutConf";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import notificationState from "../../states/notificationState";
import { useSetRecoilState } from "recoil";
import SaveToExcelButton from "../input/SaveToExcelButton";
import TkOutSourcingPurchaseDetail from "./TkOutSourcingPurchaseDetail";
import CreateRecordButton from "../input/CreateRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteSingleRecordButton from "../input/DeleteSingleRecordButton";
import ApprovalButton from "../input/ApprovalButton";
import AccessControl from "../functional/AccessControl";
import { CellType } from "@grapecity/wijmo.grid";
import { cloneDeep, debounce } from "lodash";
import defineConf from "../../config/defineConf";

function TkOutSourcingPurchase(props) {
    const id = "TkOutSourcingPurchase";
    const table = "TkOutSourcingPurchase";
    const view = "TkVwOutSourcingPurchase";

    const { t } = useTranslation();

    const [height, setHeight] = useState(0);

    const [tkOutSourcingPurchaseSchema, setTkOutSourcingPurchaseSchema] = useState(); /* schema */
    const [tkOutSourcingPurchaseUpdateSchema, setTkOutSourcingPurchaseUpdateSchema] = useState(); /* schema */

    const [tkOutSourcingPurchaseGrid, setTkOutSourcingPurchaseGrid] = useState(); /* grid */
    const [tkOutSourcingPurchaseApi, setTkOutSourcingPurchaseApi] = useState(); /* api */
    const [tkOutSourcingPurchaseData, setTkOutSourcingPurchaseData] = useState(); /* data */
    const [tkOutSourcingPurchaseDataIsEmpty, setTkOutSourcingPurchaseDataIsEmpty] = useState(); /* data empty ? */
    const [tkOutSourcingPurchaseDetailDataIsEmpty, setTkOutSourcingPurchaseDetailDataIsEmpty] =
        useState(); /* detail data empty ? */
    const [tkOutSourcingPurchaseSelect, setTkOutSourcingPurchaseSelect] = useState(); /* selected tuple */

    const authProfile = useRecoilValue(authProfileState);

    const [approvalCtx, setApprovalCtx] = useState();

    const setNotification = useSetRecoilState(notificationState);

    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    useEffect(() => {
        let mounted = true;

        window.addEventListener("resize", handleResize);
        handleResize();

        (async () => {
            let schema = await getSchema(table);

            if (mounted) {
                setTkOutSourcingPurchaseSchema(schema);
            }
        })();

        return () => {
            mounted = false;
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        let mounted = true;

        (async () => {
            if (tkOutSourcingPurchaseSchema) {
                const modifiedSchema = cloneDeep(tkOutSourcingPurchaseSchema);

                modifiedSchema.columns.forEach((column) => {
                    switch (column.name) {
                        case "companyCode":
                            column.readonly = true;

                            break;

                        default:
                            break;
                    }
                });

                if (mounted) {
                    setTkOutSourcingPurchaseUpdateSchema(modifiedSchema);
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [tkOutSourcingPurchaseSchema]);

    useEffect(() => {
        (async () => {
            if (tkOutSourcingPurchaseGrid) {
                tkOutSourcingPurchaseGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkOutSourcingPurchaseSelect(grid.selectedItems[0]);
                        } else {
                            setTkOutSourcingPurchaseSelect();
                        }
                    }),
                    10
                );

                tkOutSourcingPurchaseGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setTkOutSourcingPurchaseSelect(grid.selectedItems[0]);
                        } else {
                            setTkOutSourcingPurchaseSelect();
                        }
                    }),
                    10
                );
            }
        })();
    }, [tkOutSourcingPurchaseGrid]);

    useEffect(() => {
        let approvalCtx = {
            approvalTypeId: 19,
            tableName: table,
            statusColumn: "approvalStateId",
            schema: tkOutSourcingPurchaseSchema,
            onValidateApprovalItem: (item) => {
                if (item.createdBy !== authProfile.username) {
                    return [false, t("error.approvalRequesterNotMatch")];
                }

                return [true, ""];
            },
            makeRequestItemDescription: (item) => {
                return `${t("approvalRequestMsg.msg.eguOutSourcingPurchase", {
                    outSourcingId: item.outSourcingId,
                    outSourcingName: item.outSourcingName,
                })}`; // Use Translation on Production Code
            },
        };

        setApprovalCtx(approvalCtx);
    }, [tkOutSourcingPurchaseSchema]);

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onPartialFailure = (msg) => {
        notifyWarning(msg);
    };

    const exportFormatItem = (args) => {
        let { panel, col, row, xlsxCell } = args;

        if (panel.cellType === CellType.Cell) {
            switch (panel.columns[col].binding) {
                case "approvalState":
                    switch (tkOutSourcingPurchaseData[row][panel.columns[col].binding]) {
                        case defineConf.approvalState.PENDING:
                            xlsxCell.value = t("selectItems.approvalState.approvalPending");

                            break;

                        case defineConf.approvalState.PARTIALLY_APPROVED:
                            xlsxCell.value = t("selectItems.approvalState.partiallyApproved");

                            break;

                        case defineConf.approvalState.APPROVED:
                            xlsxCell.value = t("selectItems.approvalState.totallyApproved");

                            break;

                        case defineConf.approvalState.REJECTED:
                            xlsxCell.value = t("selectItems.approvalState.approvalRejected");

                            break;

                        case defineConf.approvalState.WITHDRAWN:
                            xlsxCell.value = t("selectItems.approvalState.approvalWithdrawn");

                            break;

                        case defineConf.approvalState.CANCELLED:
                            xlsxCell.value = t("selectItems.approvalState.approvalCancelled");

                            break;

                        default:
                            break;
                    }

                    break;

                default:
                    break;
            }
        }
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="horizontal">
                <ReflexElement flex={0.65}>
                    <BasicCrudScreenType
                        id={id}
                        headerTitle={t("term.outSourcingPurchase")}
                        table={table}
                        view={view}
                        onInitialized={(grid) => {
                            grid.selectionMode = "Row";
                            setTkOutSourcingPurchaseGrid(grid);
                        }}
                        onApiInitialized={(api) => {
                            setTkOutSourcingPurchaseApi(api);
                        }}
                        onLoadData={(data) => {
                            setTkOutSourcingPurchaseData(data);
                            setTkOutSourcingPurchaseDataIsEmpty(data.length < 1 ? true : false);
                        }}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                        onAppendButtonInFrontOfSearch={() => (
                            <BasicCrudScreenActionItemLayout>
                                <AccessControl
                                    accessTarget={tkOutSourcingPurchaseSchema && tkOutSourcingPurchaseSchema.name}
                                    accessMethod="POST"
                                >
                                    <CreateRecordButton
                                        id={id}
                                        schema={tkOutSourcingPurchaseSchema}
                                        refresh={tkOutSourcingPurchaseApi}
                                        onSuccess={onSuccess}
                                    />
                                </AccessControl>

                                <AccessControl
                                    accessTarget={tkOutSourcingPurchaseSchema && tkOutSourcingPurchaseSchema.name}
                                    accessMethod={
                                        tkOutSourcingPurchaseSchema &&
                                        (tkOutSourcingPurchaseSchema.updateWithPatch ? "PATCH" : "PUT")
                                    }
                                >
                                    <UpdateRecordButton
                                        id={id}
                                        schema={tkOutSourcingPurchaseUpdateSchema}
                                        grid={tkOutSourcingPurchaseGrid}
                                        refresh={tkOutSourcingPurchaseApi}
                                        disabled={
                                            tkOutSourcingPurchaseSelect?.approvalState ||
                                            tkOutSourcingPurchaseDataIsEmpty
                                        }
                                        //disabled={tkOutSourcingPurchaseDataIsEmpty}
                                        onSuccess={onSuccess}
                                    />
                                </AccessControl>

                                <AccessControl
                                    accessTarget={tkOutSourcingPurchaseSchema && tkOutSourcingPurchaseSchema.name}
                                    accessMethod="DELETE"
                                >
                                    <DeleteSingleRecordButton
                                        id={id}
                                        schema={tkOutSourcingPurchaseSchema}
                                        grid={tkOutSourcingPurchaseGrid}
                                        refresh={tkOutSourcingPurchaseApi}
                                        makeItemName={(row) => {
                                            return `${t("columnName.outSourcingId")}: ${row.outSourcingId}`;
                                        }}
                                        color="secondary"
                                        disabled={
                                            tkOutSourcingPurchaseSelect?.approvalState ||
                                            tkOutSourcingPurchaseDataIsEmpty ||
                                            !tkOutSourcingPurchaseDetailDataIsEmpty
                                        }
                                        //disabled={tkOutSourcingPurchaseDataIsEmpty || !tkOutSourcingPurchaseDetailDataIsEmpty}
                                        onSuccess={onSuccess}
                                        onFailure={onFailure}
                                        onPartialFailure={onPartialFailure}
                                    />
                                </AccessControl>

                                <AccessControl
                                    accessTarget={tkOutSourcingPurchaseSchema && tkOutSourcingPurchaseSchema.name}
                                    accessMethod="APPROVAL"
                                >
                                    <ApprovalButton
                                        id={id}
                                        grid={tkOutSourcingPurchaseGrid}
                                        refresh={tkOutSourcingPurchaseApi}
                                        approvalCtx={approvalCtx}
                                        disabled={
                                            tkOutSourcingPurchaseDataIsEmpty || tkOutSourcingPurchaseDetailDataIsEmpty
                                        }
                                        onSuccess={onSuccess}
                                        onFailure={onFailure}
                                    />
                                </AccessControl>

                                <AccessControl
                                    accessTarget={tkOutSourcingPurchaseSchema && tkOutSourcingPurchaseSchema.name}
                                    accessMethod="SAVETOEXCEL"
                                >
                                    <SaveToExcelButton
                                        id={id}
                                        grid={tkOutSourcingPurchaseGrid}
                                        view={view}
                                        color="primary"
                                        exportFormatItem={exportFormatItem}
                                        disabled={tkOutSourcingPurchaseDataIsEmpty}
                                    />
                                </AccessControl>
                            </BasicCrudScreenActionItemLayout>
                        )}
                    />
                </ReflexElement>

                <ReflexSplitter style={{ height: layoutConf.reflex.splitterHeight }} />

                <ReflexElement flex={0.35}>
                    {tkOutSourcingPurchaseSelect ? (
                        <TkOutSourcingPurchaseDetail
                            id={`${id}-detail`}
                            onSelectMst={tkOutSourcingPurchaseSelect}
                            onDetailEmpty={(isDetailEmpty) => {
                                setTkOutSourcingPurchaseDetailDataIsEmpty(isDetailEmpty);
                            }}
                        />
                    ) : (
                        <></>
                    )}
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
}

export default TkOutSourcingPurchase;
